import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { interval, map } from "rxjs";

import { PATHS } from "@app/core/constants/path.const";
import {
  ENotificationEntityType,
  TNotification,
  TNotificationStrategy,
} from "@app/core/notifications/types/notification.type";
import {
  POLICY_VERSION_DOCUMENTS_TAB_QUERY_VALUE,
  POLICY_VERSION_TAB_QUERY_PARAM,
} from "@app/features/policies/types/policy.type";
import { TimeAgoPipe } from "@app/shared/pipes/time-ago.pipe";
import { TranslatePipe } from "@ngx-translate/core";

const RELATIVE_TIME_REFRESH_MS = 60_000;

const COI_GENERATION_NOTIFICATION_TYPE_NAME = "Policy COI Generation";

function buildPolicyVersionUrl(notification: TNotification): string {
  const url = `${PATHS.policies.path}/${PATHS.policies.versionDetails.path}/${notification.entityId}`;

  return notification.notificationTypeName === COI_GENERATION_NOTIFICATION_TYPE_NAME
    ? `${url}?${POLICY_VERSION_TAB_QUERY_PARAM}=${POLICY_VERSION_DOCUMENTS_TAB_QUERY_VALUE}`
    : url;
}

const NOTIFICATION_STRATEGY_MAP = new Map<
  ENotificationEntityType,
  (notification: TNotification) => TNotificationStrategy
>([
  [
    ENotificationEntityType.PolicyVersion,
    (notification: TNotification) => ({
      ...notification,
      buildURL: buildPolicyVersionUrl(notification),
      action: "REDIRECT",
      actionLabel: "NOTIFICATIONS.ACTIONS.OPEN_POLICY",
    }),
  ],
  [
    ENotificationEntityType.Client,
    (notification: TNotification) => ({
      ...notification,
      buildURL: `${PATHS.clients.path}/${notification.entityId}`,
      action: "REDIRECT",
      actionLabel: "NOTIFICATIONS.ACTIONS.OPEN_CLIENT",
    }),
  ],
  [
    ENotificationEntityType.Invoice,
    (notification: TNotification) => ({
      ...notification,
      buildURL: `${PATHS.billing.path}/${PATHS.billing.invoices.path}/${notification.entityId}`,
      action: "REDIRECT",
      actionLabel: "NOTIFICATIONS.ACTIONS.OPEN_INVOICE",
    }),
  ],
  [
    ENotificationEntityType.Claim,
    (notification: TNotification) => ({
      ...notification,
      buildURL: `${PATHS.claims.path}/${PATHS.claims.directory.path}/${notification.entityId}`,
      action: "REDIRECT",
      actionLabel: "NOTIFICATIONS.ACTIONS.OPEN_CLAIM",
    }),
  ],
  [
    ENotificationEntityType.DocumentExport,
    (notification: TNotification) => ({
      ...notification,
      buildURL: `${PATHS.documents.path}/${PATHS.documents.exports.path}/${notification.entityId}`,
      action: "REDIRECT",
      actionLabel: "NOTIFICATIONS.ACTIONS.DOWNLOAD_EXPORT",
    }),
  ],
  [
    ENotificationEntityType.Policy,
    (notification: TNotification) => ({
      ...notification,
      buildURL: null,
      action: "NONE",
      actionLabel: null,
    }),
  ],
  [
    ENotificationEntityType.AdminProductRevision,
    (notification: TNotification) => ({
      ...notification,
      buildURL: `${PATHS.product.path}/${PATHS.product.admin.path}/${PATHS.product.admin.viewDetailsRevision.path}/${notification.entityId}`,
      action: "REDIRECT",
      actionLabel: "NOTIFICATIONS.ACTIONS.OPEN_ADMIN_PRODUCT_REVISION",
    })
  ],
]);

function transformNotification(notification: TNotification): TNotificationStrategy {
  const notificationStrategy = NOTIFICATION_STRATEGY_MAP.get(notification.entityType);

  if (notification.entityId && notificationStrategy) {
    return notificationStrategy(notification);
  }

  return { ...notification, buildURL: null, action: "NONE", actionLabel: null };
}

@Component({
  selector: "under-ui-notification-item",
  templateUrl: "./notification-item.component.html",
  styleUrl: "./notification-item.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatIconModule, MatTooltipModule, TimeAgoPipe, TranslatePipe],
  standalone: true,
})
export class NotificationItemComponent {
  @Input({ required: true, transform: transformNotification }) notification!: TNotificationStrategy;

  @Output() executeAction = new EventEmitter<TNotificationStrategy>();
  @Output() markAsRead = new EventEmitter<TNotificationStrategy>();

  protected readonly now = toSignal(interval(RELATIVE_TIME_REFRESH_MS).pipe(map(() => Date.now())), {
    initialValue: Date.now(),
  });
}
